// C3 — the "Flag a problem" form.
//
// Available from the in-record Cleanup drawer for ANY record,
// including records with zero detected issues (the service blueprint
// is explicit: a 0-issue record is "still usable for manual actions /
// flag"). Submitting creates a real manual CleanupIssue that flows
// through the same research → decide → undo → audit engine, so a flag
// is never a dead-end note — it's a tracked, assignable, resolvable
// issue.
//
// Three inputs, in order of the service blueprint: pick a type (or
// "Not sure — needs a look"), an optional assignee (defaults to
// Unassigned — auto-routing to a domain owner is a documented later
// refinement; contacts/households have no single owner anyway), and a
// REQUIRED one-line note. Async by design: on submit the issue enters
// the queue 'new' and the form closes — no forced resolution.

import { useEffect, useState } from "react";

import {
  MANUAL_PROBLEM_TYPES,
  type ManualProblemType,
} from "../../../shared/db/cleanup-manual-issue";
import { api, ApiError, type AssignableUser } from "../api";

const FIELD_LABEL: React.CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--blue-dark)",
  marginBottom: 5,
};

const INPUT: React.CSSProperties = {
  width: "100%",
  border: "1px solid var(--mid-gray)",
  borderRadius: 8,
  padding: "8px 10px",
  fontSize: 13.5,
  color: "var(--navy)",
  background: "var(--bg-0)",
  boxSizing: "border-box",
};

export function FlagProblem({
  entityKind,
  entityId,
  entityLabel,
  onCreated,
  onCancel,
}: {
  entityKind: string;
  entityId: string;
  entityLabel: string;
  onCreated: (issueId: string) => void;
  onCancel: () => void;
}) {
  const [problemType, setProblemType] = useState<ManualProblemType | "">(
    "",
  );
  const [note, setNote] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [users, setUsers] = useState<AssignableUser[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await api.listAssignableUsers();
        if (!cancelled) setUsers(r.users);
      } catch {
        // Assignee is optional — a failed list just means the flag
        // is created Unassigned (UX rule #5: no dead end).
        if (!cancelled) setUsers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selected = MANUAL_PROBLEM_TYPES.find((t) => t.value === problemType);
  const canSubmit =
    problemType !== "" && note.trim().length > 0 && !submitting;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await api.createCleanupManualIssue({
        entity_kind: entityKind,
        entity_id: entityId,
        entity_label: entityLabel,
        problem_type: problemType as string,
        note: note.trim(),
        assigned_owner_user_id: assigneeId || null,
      });
      onCreated(r.issue_id);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.message
          : "Couldn't create the flag. Try again.",
      );
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        border: "1px solid var(--blue)",
        background: "var(--blue-tint)",
        borderRadius: 10,
        padding: 14,
      }}
    >
      <div
        style={{
          fontSize: 14,
          fontWeight: 800,
          color: "var(--navy)",
          marginBottom: 2,
        }}
      >
        Flag a problem on {entityLabel}
      </div>
      <div
        style={{ fontSize: 12.5, color: "var(--fg-3)", marginBottom: 12 }}
      >
        Creates a tracked cleanup issue — it'll appear in the queue and
        on this record for someone to research and resolve. Not a
        dead-end note.
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={FIELD_LABEL} htmlFor="cu-flag-type">
          What's wrong?
        </label>
        <select
          id="cu-flag-type"
          value={problemType}
          onChange={(e) =>
            setProblemType(e.target.value as ManualProblemType | "")
          }
          style={INPUT}
        >
          <option value="">Select what's wrong…</option>
          {MANUAL_PROBLEM_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        {selected && (
          <div
            style={{ fontSize: 12, color: "var(--fg-muted)", marginTop: 4 }}
          >
            {selected.hint}
          </div>
        )}
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={FIELD_LABEL} htmlFor="cu-flag-note">
          What did you notice? <span style={{ color: "var(--danger)" }}>*</span>
        </label>
        <textarea
          id="cu-flag-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder="One or two lines so whoever picks this up knows what to look at."
          style={{ ...INPUT, resize: "vertical", font: "inherit" }}
        />
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={FIELD_LABEL} htmlFor="cu-flag-assignee">
          Assign to (optional)
        </label>
        <select
          id="cu-flag-assignee"
          value={assigneeId}
          onChange={(e) => setAssigneeId(e.target.value)}
          style={INPUT}
        >
          <option value="">Unassigned — anyone can pick it up</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name ?? u.email}
              {u.role_slug ? ` · ${u.role_slug}` : ""}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div
          style={{
            border: "1px solid var(--danger)",
            background: "rgba(192,57,43,0.06)",
            color: "var(--navy)",
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: 12.5,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button
          onClick={onCancel}
          disabled={submitting}
          style={{
            border: "1px solid var(--mid-gray)",
            background: "var(--bg-0)",
            color: "var(--navy)",
            borderRadius: 8,
            padding: "7px 14px",
            fontSize: 13,
            fontWeight: 700,
            cursor: submitting ? "default" : "pointer",
          }}
        >
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={!canSubmit}
          title={
            problemType === ""
              ? "Pick what's wrong first"
              : note.trim().length === 0
                ? "A note is required"
                : "Create the flag"
          }
          style={{
            border: "1px solid var(--blue)",
            background: canSubmit ? "var(--blue)" : "var(--mid-gray)",
            color: "var(--white)",
            borderRadius: 8,
            padding: "7px 16px",
            fontSize: 13,
            fontWeight: 800,
            cursor: canSubmit ? "pointer" : "default",
          }}
        >
          {submitting ? "Flagging…" : "Flag this problem"}
        </button>
      </div>
    </div>
  );
}
